import { doc, docData, Firestore } from '@angular/fire/firestore';
import { map, Observable, shareReplay, switchMap, throttleTime } from 'rxjs';

import { BaseModel } from '../../global/models';
import { ApiServerService } from '../api';
import { defaultElasticConverter } from './elastic.helper';
import {
  ElasticConverter, ElasticFirestoreOptions,
  ElasticQueryOptions,
  ElasticResponse,
  ElasticResult, ElasticSearchBody,
} from './elastic.models';
import { FirestoreBase } from './firestore-base';


/**
 * Base class extended with ElasticSearch capabilities.
 * Suitable for services that require complex searches, real-time filtering, or aggregations.
 * @template Model - The data model type, which must extend `BaseModel`.
 * @example
 * // Implementation in a domain service (CouponDbService)
 * export class CouponDbService extends ElasticFirestoreBase<Coupon> {
 *   constructor(firestore: Firestore, apiServer: ApiServerService) {
 *     super('coupons', firestore, apiServer, {
 *       dateProperties: ['expirationDate'],
 *       // Optional: Custom Elastic converter
 *       elasticConverter: couponElasticConverter
 *     });
 *   }
 * }
 * @example
 * // Example of a custom ElasticConverter
 * const couponElasticConverter: ElasticConverter<Coupon> = {
 *   fromElastic: (item: any): Coupon => {
 *     return {
 *       ...item,
 *       // Custom logic: ensure field consistency or computed properties
 *       isExpired: new Date(item.expirationDate) < new Date()
 *     };
 *   }
 * };
 */
export abstract class ElasticFirestoreBase<Model extends BaseModel> extends FirestoreBase<Model> {

  // Elastic converter for the class model.
  protected elasticConverter: ElasticConverter<Model>;

  constructor(
    protected override  collectionName: string,
    protected override firestore: Firestore,
    protected apiServerService: ApiServerService,
    protected override options: ElasticFirestoreOptions<Model> = {}
  ) {
    // 1. Initialize FirestoreBase (dateProperties, allowGetAll, etc.)
    super(collectionName, firestore, options);

    // 2. Assign the converter: store the converter on the instance to use
    // as the default for searches.
    this.elasticConverter = options.elasticConverter || defaultElasticConverter<Model>(this.dateProperties);
  }

  /**
   * Listen to a 'refresh' document in Firestore for a specific index.
   * Used as a trigger to notify when data has changed.
   * @param {ElasticQueryOptions} [options] - Configuration options.
   * @param {string} [options.index] - Index/collection name to watch. Defaults to `this.collectionName`.
   * @param {number} [options.throttleDuration] - Throttle time in milliseconds. Defaults to `2000`.
   * @returns {Observable<Date | null>} Observable that emits the last update date.
   * @example
   * // Watch default collection with 5 seconds throttle
   * this.onChange({ throttleDuration: 5000 });
   * @example
   * // Watch a specific sub-index
   * this.onChange({ index: 'log_index' });
   */
  onChange(options: { index?: string; throttleDuration?: number; } = {}): Observable<Date | null> {
    return this.run(() => {
      const {
        index,
        throttleDuration = 2000
      } = options;

      const targetIndex = index || this.collectionName;
      const docRef = doc(this.firestore, 'refresh', targetIndex);

      return docData(docRef).pipe(
        throttleTime(throttleDuration, undefined, { leading: true, trailing: true }),
        map((data: any) => data?.lastUpdate ? data.lastUpdate.toDate() : null)
      );
    });
  }

  /**
   * Execute a search query against ElasticSearch with support for sub-indices.
   * @template T - Expected model type for results. Defaults to the class `Model`.
   * @param {ElasticSearchBody} [body={}] - Query DSL payload including filters, sorting and pagination.
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @param {ElasticConverter<T>} [options.converter] - Custom converter. Considered only if `index` is specified.
   * @returns {Promise<ElasticResult<T>>} Promise resolving with transformed hits, total and metadata.
   * @throws {Error} If the API server call fails.
   */
  async search<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Promise<ElasticResult<T>> {
    try {
      const { index: optIndex, converter: optConverter } = (options || {}) as ElasticQueryOptions<T>;
      const index = optIndex || this.collectionName;
      const converter = optIndex
        ? (optConverter as unknown as ElasticConverter<T> || defaultElasticConverter<T>())
        : (this.elasticConverter as unknown as ElasticConverter<T>);

      const response = await this.apiServerService.search<ElasticResponse<T>>({ index, body });

      const items = (response?.hits?.hits || []).map(hit => {
        return converter.fromElastic({ ...hit._source, id: hit._id });
      });

      return {
        items,
        total: response?.hits?.total?.value ?? 0,
        from: body.from ?? 0,
        size: body.size ?? items.length,
        took: response?.took ?? 0,
        aggregations: response?.aggregations as any
      };

    } catch (error) {
      console.error(`[ElasticFirestoreBase] Error en search para el índice:`, error);
      throw error;
    }
  };

  /**
   * Reactive version of `search()`.
   * Subscribes to collection changes via `onChange()` and re-runs the ElasticSearch
   * query each time the refresh document is updated.
   * @template T - Expected model type. Defaults to the class `Model`.
   * @param {ElasticSearchBody} [body={}] - Query DSL payload.
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @param {ElasticConverter<T>} [options.converter] - Custom converter. Considered only if `index` is specified.
   * @param {number} [options.throttleDuration] - Throttle time in milliseconds. Defaults to `2000`.
   * @returns {Observable<ElasticResult<T>>} Observable emitting a new result when data changes.
   */
  search$<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Observable<ElasticResult<T>> {
    // Extract the index from options for onChange
    const targetIndex = (options?.index) || this.collectionName;

    return this.onChange({ index: targetIndex, throttleDuration: options?.throttleDuration }).pipe(
      switchMap(() => this.search<T>(body, options)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Get aggregations and total count (without hits) from ElasticSearch.
   * Useful for dashboards, KPIs or initializing filter components.
   * @template T - Reference model type for the index.
   * @param {ElasticSearchBody} [body={}] - Query payload. `size: 0` is enforced internally.
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @returns {Promise<Pick<ElasticResult<T>, 'total' | 'aggregations' | 'took'>>} Promise with statistics.
   */
  async aggs<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Promise<Pick<ElasticResult<T>, 'total' | 'aggregations' | 'took'>> {

    const result = await this.search<T>({ ...body, size: 0 }, options);

    return {
      total: result.total,
      aggregations: result.aggregations,
      took: result.took
    };
  }

  /**
   * Reactive version of `aggs()`.
   * Keeps totals and aggregations up to date in real time based on `onChange()`.
   * @template T - Reference model type.
   * @param {ElasticSearchBody} [body={}] - Query payload (enforces `size: 0`).
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @param {number} [options.throttleDuration] - Throttle time in milliseconds. Defaults to `2000`.
   * @returns {Observable<Pick<ElasticResult<T>, 'total' | 'aggregations' | 'took'>>} Observable with updated stats.
   */
  aggs$<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Observable<Pick<ElasticResult<T>, 'total' | 'aggregations' | 'took'>> {
    const targetIndex = (options?.index) || this.collectionName;

    return this.onChange({ index: targetIndex, throttleDuration: options?.throttleDuration }).pipe(
      switchMap(() => this.aggs<T>(body, options)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Get accurate total count of documents matching a query.
   * Uses `size: 0` and `track_total_hits: true` to obtain the real count without downloading documents.
   * @template T - Reference model type for the index.
   * @param {ElasticSearchBody} [body={}] - Query payload to filter documents.
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @returns {Promise<number>} Promise resolving with the total number of documents found.
   */
  async count<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Promise<number> {
    // Combine the original body with count optimizations
    const countBody: ElasticSearchBody = {
      ...body,
      size: 0,
      track_total_hits: true
    };

    const result = await this.search<T>(countBody, options);

    return result.total;
  }

  /**
   * Reactive version of `count()`.
   * Returns an Observable that emits the total count each time the ElasticSearch index
   * (or the Firestore refresh document) detects a change.
   * @template T - Reference model type for the index.
   * @param {ElasticSearchBody} [body={}] - Query payload to filter documents.
   * @param {ElasticQueryOptions<T>} [options] - Optional configuration for the search.
   * @param {string} [options.index] - Target index name. Defaults to `collectionName`.
   * @param {ElasticConverter<T>} [options.converter] - Custom converter. Considered only if `index` is specified.
   * @param {number} [options.throttleDuration] - Throttle time in milliseconds. Defaults to `2000`.
   * @returns {Observable<number>} Observable that emits the updated total count.
   */
  count$<T extends BaseModel = Model>(
    body: ElasticSearchBody = {},
    options?: ElasticQueryOptions<T>
  ): Observable<number> {
    // Determine the target index to listen for correct changes
    const targetIndex = (options?.index) || this.collectionName;

    return this.onChange({ index: targetIndex, throttleDuration: options?.throttleDuration }).pipe(
      // Each time there is a change, execute the optimized count()
      switchMap(() => this.count<T>(body, options)),
      // Share the execution and keep the last value for new subscribers
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
